// editor.js — 自作台のエディタ
//
// 盤面の論理座標（450×800）をそのまま扱う。ゲーム側の派手な描画とは別に、
// ここでは「どこに何があるか」だけが分かる素の絵を描く。
//
// 触れるのは釘・壁・仕切り・風車・入賞口の位置だけで、外周や発射レール、
// 液晶といった骨格は固定にしてある。そこを動かせるようにすると、盤面として
// 成立しない台（玉が出てこない・戻ってこない）が簡単に作れてしまうため。

import {
  W, H, PEG_R, BALL_R, FRAME_SEGMENTS, POCKET_DEFS, LCD,
  createBoard, defaultLayout, dialToPower, inPlayfield,
} from './board.js';
import { stepWorld, launchBall } from './physics.js';
import {
  VIDEO_STATES, BUILTIN_VIDEOS, ICON_CHOICES,
  sanitize, toRecord, inspect, videoStateLabel, builtinName,
} from './custom.js';
import { videoKey } from './storage.js';
import { estimatePayout } from './machines.js';
import { t, num, assetUrl } from './i18n.js';

const TAU = Math.PI * 2;
const GRID = 5;
const MIRROR_AXIS = W / 2;

// 掴める距離。指で触っても取りこぼさない程度に緩くしてある。
const HIT_PEG = PEG_R + 6;
const HIT_NODE = 10;
const HIT_LINE = 8;

const TOOLS = ['select', 'peg', 'wall', 'mill', 'erase'];

/** 試し打ち診断で流す玉の数（左打ち・右打ちで各 N 発） */
const DIAG_BALLS = 100;

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const r1 = (v) => Math.round(v * 10) / 10;

export class Editor {
  /**
   * @param {HTMLElement} root
   * @param {object} handlers {onExit, onSave, onTest}
   */
  constructor(root, handlers = {}) {
    this.root = root;
    this.on = handlers;
    this.visible = false;
    this.m = null;
    this.tool = 'select';
    this.grid = true;
    this.mirror = false;
    this.tab = 'board';
    this.sel = null;            // 選択中のもの {kind, ...}
    this.drag = null;           // ドラッグ中の状態
    this.draft = null;          // 作図中の壁
    this.history = [];
    this.future = [];
    this.pendingFiles = {};     // 保存するまで抱えておくアップロード済みファイル
    this.previewUrls = [];
    this.diagBusy = false;
    this._build();
  }

  // ---------------------------------------------------------------- 構築

  _build() {
    this.root.innerHTML = `
      <div class="ed-head">
        <button class="ed-btn ed-back" type="button">${esc(t('editor.back'))}</button>
        <input class="ed-name" type="text" maxlength="24" aria-label="${esc(t('editor.nameAria'))}">
        <button class="ed-btn ed-test" type="button">${esc(t('editor.test'))}</button>
        <button class="ed-btn ed-save" type="button">${esc(t('editor.save'))}</button>
      </div>
      <div class="ed-body">
        <div class="ed-stage"><canvas class="ed-canvas" aria-label="${esc(t('editor.boardAria'))}"></canvas></div>
        <div class="ed-side">
          <nav class="ed-tabs">
            ${['board', 'look', 'spec', 'video'].map((k) =>
              `<button type="button" data-tab="${k}">${esc(t(`editor.tab.${k}`))}</button>`).join('')}
          </nav>
          <div class="ed-pane"></div>
        </div>
      </div>`;

    this.canvas = this.root.querySelector('.ed-canvas');
    this.ctx = this.canvas.getContext('2d');
    this.pane = this.root.querySelector('.ed-pane');
    this.nameEl = this.root.querySelector('.ed-name');

    this.root.querySelector('.ed-back').addEventListener('click', () => this._exit());
    this.root.querySelector('.ed-test').addEventListener('click', () => {
      this.m.name = this.nameEl.value.trim() || t('custom.fallbackName');
      this.on.onTest?.(this.m, this.pendingFiles);
    });
    this.root.querySelector('.ed-save').addEventListener('click', () => this._save());
    this.nameEl.addEventListener('input', () => { this.m.name = this.nameEl.value; });

    for (const b of this.root.querySelectorAll('.ed-tabs button')) {
      b.addEventListener('click', () => { this.tab = b.dataset.tab; this._syncTabs(); });
    }

    this.canvas.addEventListener('pointerdown', (e) => this._down(e));
    this.canvas.addEventListener('pointermove', (e) => this._move(e));
    for (const ev of ['pointerup', 'pointercancel']) {
      this.canvas.addEventListener(ev, (e) => this._up(e));
    }
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => { this._resize(); this._render(); })
        .observe(this.root.querySelector('.ed-stage'));
    }
    window.addEventListener('resize', () => { this._resize(); this._render(); });
  }

  _resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const r = this.canvas.getBoundingClientRect();
    const w = r.width || W, h = r.height || H;
    this.canvas.width = Math.max(1, Math.round(w * dpr));
    this.canvas.height = Math.max(1, Math.round(h * dpr));
    this.scale = Math.min(w / W, h / H) * dpr;
    this.offX = (this.canvas.width - W * this.scale) / 2;
    this.offY = (this.canvas.height - H * this.scale) / 2;
  }

  // ---------------------------------------------------------------- 開閉

  /** 台を開く。編集は複製に対してやるので、やめれば元のまま残る */
  open(machine) {
    this.m = sanitize(toRecord(machine));
    this.history = [];
    this.future = [];
    this.pendingFiles = {};
    this.sel = null;
    this.draft = null;
    this.tab = 'board';
    this.visible = true;
    this.root.classList.remove('gone');
    this.nameEl.value = this.m.name;
    this._syncTabs();
    this._resize();
    this._render();
  }

  close() {
    this.visible = false;
    this.root.classList.add('gone');
    this._clearPreviews();
  }

  /** 試し打ちから戻ってきたとき。編集中の内容をそのまま続ける */
  reopen() {
    if (!this.m) return false;
    this.visible = true;
    this.root.classList.remove('gone');
    this._syncTabs();
    this._resize();
    this._render();
    return true;
  }

  _exit() {
    const dirty = this.history.length > 0 || Object.keys(this.pendingFiles).length > 0;
    if (dirty && !confirm(t('editor.confirmExit'))) return;
    this.close();
    this.on.onExit?.();
  }

  async _save() {
    this.m.name = this.nameEl.value.trim() || t('custom.fallbackName');
    await this.on.onSave?.(this.m, this.pendingFiles);
    this.pendingFiles = {};
    this.history = [];
  }

  // ---------------------------------------------------------------- 履歴

  _snapshot() {
    this.history.push(JSON.stringify(this.m.layout));
    if (this.history.length > 60) this.history.shift();
    this.future.length = 0;
  }

  _undo() {
    if (!this.history.length) return;
    this.future.push(JSON.stringify(this.m.layout));
    this.m.layout = JSON.parse(this.history.pop());
    this.sel = null;
    this._render();
    this._syncTabs();
  }

  _redo() {
    if (!this.future.length) return;
    this.history.push(JSON.stringify(this.m.layout));
    this.m.layout = JSON.parse(this.future.pop());
    this.sel = null;
    this._render();
    this._syncTabs();
  }

  // ---------------------------------------------------------------- 入力

  _toBoard(e) {
    const r = this.canvas.getBoundingClientRect();
    const dpr = this.canvas.width / r.width;
    return {
      x: ((e.clientX - r.left) * dpr - this.offX) / this.scale,
      y: ((e.clientY - r.top) * dpr - this.offY) / this.scale,
    };
  }

  _snap(p) {
    if (!this.grid) return { x: r1(p.x), y: r1(p.y) };
    return { x: Math.round(p.x / GRID) * GRID, y: Math.round(p.y / GRID) * GRID };
  }

  _down(e) {
    if (!this.visible) return;
    e.preventDefault();
    this.canvas.setPointerCapture?.(e.pointerId);
    const p = this._toBoard(e);

    if (this.tool === 'select') {
      const hit = this._pick(p);
      this.sel = hit;
      if (hit) {
        this._snapshot();
        this.drag = { kind: 'move', hit, from: p, moved: false };
      }
      this._render();
      this._syncTabs();
      return;
    }

    if (this.tool === 'peg') {
      this._snapshot();
      this.drag = { kind: 'paint', last: null };
      this._paintPeg(p);
      return;
    }

    if (this.tool === 'mill') {
      const q = this._snap(p);
      if (!inPlayfield(q.x, q.y)) return;
      this._snapshot();
      this.m.layout.windmills.push({
        x: q.x, y: q.y, r: 22, blades: 4, speed: 0.03, dir: 1, phase: 0,
      });
      this._render();
      this._syncTabs();
      return;
    }

    if (this.tool === 'wall') {
      const q = this._snap(p);
      this.draft = { x1: q.x, y1: q.y, x2: q.x, y2: q.y };
      this.drag = { kind: 'wall' };
      this._render();
      return;
    }

    if (this.tool === 'erase') {
      this._snapshot();
      this.drag = { kind: 'erase' };
      this._eraseAt(p);
    }
  }

  _move(e) {
    if (!this.visible || !this.drag) return;
    const p = this._toBoard(e);

    if (this.drag.kind === 'paint') { this._paintPeg(p); return; }
    if (this.drag.kind === 'erase') { this._eraseAt(p); return; }
    if (this.drag.kind === 'wall') {
      const q = this._snap(p);
      this.draft.x2 = q.x; this.draft.y2 = q.y;
      this._render();
      return;
    }
    if (this.drag.kind === 'move') {
      this.drag.moved = true;
      this._applyMove(this.drag.hit, this._snap(p));
      this._render();
    }
  }

  _up() {
    if (!this.drag) return;
    if (this.drag.kind === 'wall' && this.draft) {
      const d = Math.hypot(this.draft.x2 - this.draft.x1, this.draft.y2 - this.draft.y1);
      if (d >= 10) {
        this._snapshot();
        this.m.layout.walls.push({ ...this.draft });
        if (this.mirror) this.m.layout.walls.push(mirrorWall(this.draft));
      }
      this.draft = null;
    }
    // 動かさずに離しただけなら履歴を汚さない
    if (this.drag.kind === 'move' && !this.drag.moved) this.history.pop();
    this.drag = null;
    this._render();
    this._syncTabs();
  }

  /** キー操作。開いている間だけ main.js から回してもらう */
  onKeyDown(e) {
    if (!this.visible) return false;
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement
      || e.target instanceof HTMLSelectElement) return false;

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      if (e.shiftKey) this._redo(); else this._undo();
      return true;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') { this._deleteSelection(); return true; }
    if (e.key === 'Escape') { this._exit(); return true; }
    return false;
  }

  // ---------------------------------------------------------------- 編集操作

  _paintPeg(p) {
    const q = this._snap(p);
    if (!inPlayfield(q.x, q.y)) return;
    // なぞったときに団子にならないよう、玉が通れる間隔を空けて置く
    const last = this.drag.last;
    if (last && Math.hypot(q.x - last.x, q.y - last.y) < BALL_R * 2 + PEG_R) return;
    if (this._pegAt(q.x, q.y)) return;

    this.m.layout.pegs.push({ x: q.x, y: q.y, type: 'peg' });
    if (this.mirror) {
      const mx = r1(MIRROR_AXIS * 2 - q.x);
      if (Math.abs(mx - q.x) > 1 && inPlayfield(mx, q.y) && !this._pegAt(mx, q.y)) {
        this.m.layout.pegs.push({ x: mx, y: q.y, type: 'peg' });
      }
    }
    this.drag.last = q;
    this._render();
  }

  _pegAt(x, y, tol = PEG_R * 2) {
    return this.m.layout.pegs.find((p) => Math.hypot(p.x - x, p.y - y) < tol);
  }

  _eraseAt(p) {
    const hit = this._pick(p, { pockets: false });
    if (!hit) return;
    this._remove(hit);
    if (this.mirror && hit.kind === 'peg') {
      const peg = hit.obj;
      const twin = this._pegAt(MIRROR_AXIS * 2 - peg.x, peg.y, PEG_R + 3);
      if (twin) this._remove({ kind: 'peg', obj: twin });
    }
    this._render();
  }

  _deleteSelection() {
    if (!this.sel || this.sel.kind === 'pocket') return;
    this._snapshot();
    this._remove(this.sel);
    this.sel = null;
    this._render();
    this._syncTabs();
  }

  _remove(hit) {
    const L = this.m.layout;
    const drop = (arr, obj) => { const i = arr.indexOf(obj); if (i >= 0) arr.splice(i, 1); };
    if (hit.kind === 'peg') drop(L.pegs, hit.obj);
    else if (hit.kind === 'wall') drop(L.walls, hit.obj);
    else if (hit.kind === 'mill') drop(L.windmills, hit.obj);
    else if (hit.kind === 'sep') drop(L.separators, hit.obj);
  }

  _applyMove(hit, q) {
    if (hit.kind === 'peg') {
      if (!inPlayfield(q.x, q.y)) return;
      hit.obj.x = q.x; hit.obj.y = q.y;
    } else if (hit.kind === 'mill') {
      if (!inPlayfield(q.x, q.y)) return;
      hit.obj.x = q.x; hit.obj.y = q.y;
    } else if (hit.kind === 'pocket') {
      // 入賞口はセンサーごと動く。盤面の外に出すと拾えなくなるので抑える
      hit.obj.x = clamp(q.x, 45, 405);
      hit.obj.y = clamp(q.y, 345, 700);
    } else if (hit.kind === 'wall') {
      const w = hit.obj;
      if (hit.node === 1) { w.x1 = q.x; w.y1 = q.y; }
      else if (hit.node === 2) { w.x2 = q.x; w.y2 = q.y; }
      else {
        const dx = q.x - hit.grabX, dy = q.y - hit.grabY;
        w.x1 = r1(hit.base.x1 + dx); w.y1 = r1(hit.base.y1 + dy);
        w.x2 = r1(hit.base.x2 + dx); w.y2 = r1(hit.base.y2 + dy);
      }
    } else if (hit.kind === 'sep') {
      const s = hit.obj;
      if (hit.node === 1) s.y1 = clamp(q.y, 110, s.y2 - 20);
      else if (hit.node === 2) s.y2 = clamp(q.y, s.y1 + 20, 790);
      else {
        const dy = q.y - hit.grabY;
        s.x = clamp(q.x, 40, 410);
        s.y1 = clamp(r1(hit.base.y1 + dy), 110, 770);
        s.y2 = clamp(r1(hit.base.y2 + dy), s.y1 + 20, 790);
      }
    }
  }

  /** 座標にいちばん近い編集対象を返す。細かい掴み手を優先する */
  _pick(p, { pockets = true } = {}) {
    const L = this.m.layout;

    for (const w of L.walls) {
      if (Math.hypot(w.x1 - p.x, w.y1 - p.y) < HIT_NODE) return { kind: 'wall', obj: w, node: 1 };
      if (Math.hypot(w.x2 - p.x, w.y2 - p.y) < HIT_NODE) return { kind: 'wall', obj: w, node: 2 };
    }
    for (const s of L.separators) {
      if (Math.abs(p.x - s.x) < s.r + 6 && Math.abs(p.y - s.y1) < HIT_NODE) return { kind: 'sep', obj: s, node: 1 };
      if (Math.abs(p.x - s.x) < s.r + 6 && Math.abs(p.y - s.y2) < HIT_NODE) return { kind: 'sep', obj: s, node: 2 };
    }
    let best = null, bestD = HIT_PEG;
    for (const g of L.pegs) {
      const d = Math.hypot(g.x - p.x, g.y - p.y);
      if (d < bestD) { bestD = d; best = { kind: 'peg', obj: g }; }
    }
    if (best) return best;

    for (const wm of L.windmills) {
      if (Math.hypot(wm.x - p.x, wm.y - p.y) < wm.r) return { kind: 'mill', obj: wm };
    }
    if (pockets) {
      for (const d of POCKET_DEFS) {
        const pos = L.pockets[d.id];
        if (Math.hypot(pos.x - p.x, pos.y - p.y) < 20) {
          return { kind: 'pocket', obj: pos, id: d.id, def: d };
        }
      }
    }
    for (const w of L.walls) {
      if (distToSeg(p.x, p.y, w) < HIT_LINE) {
        return { kind: 'wall', obj: w, node: 0, grabX: p.x, grabY: p.y, base: { ...w } };
      }
    }
    for (const s of L.separators) {
      if (Math.abs(p.x - s.x) < s.r + 4 && p.y > s.y1 - s.r && p.y < s.y2) {
        return { kind: 'sep', obj: s, node: 0, grabX: p.x, grabY: p.y, base: { ...s } };
      }
    }
    return null;
  }

  // ---------------------------------------------------------------- 描画

  _render() {
    if (!this.visible || !this.m) return;
    const ctx = this.ctx;
    const L = this.m.layout;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(this.scale, 0, 0, this.scale, this.offX, this.offY);

    const grd = ctx.createLinearGradient(0, 0, 0, H);
    grd.addColorStop(0, `hsl(${this.m.hue} 45% 11%)`);
    grd.addColorStop(1, '#05060f');
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, W, H);

    if (this.grid) {
      ctx.strokeStyle = 'rgba(120,170,255,0.09)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 0; x <= W; x += 25) { ctx.moveTo(x, 0); ctx.lineTo(x, H); }
      for (let y = 0; y <= H; y += 25) { ctx.moveTo(0, y); ctx.lineTo(W, y); }
      ctx.stroke();
    }
    if (this.mirror) {
      ctx.strokeStyle = 'rgba(255,110,199,0.35)';
      ctx.setLineDash([6, 6]);
      ctx.beginPath(); ctx.moveTo(MIRROR_AXIS, 0); ctx.lineTo(MIRROR_AXIS, H); ctx.stroke();
      ctx.setLineDash([]);
    }

    // 骨格（触れない部分）は控えめに
    ctx.lineCap = 'round';
    ctx.lineWidth = 2;
    for (const s of FRAME_SEGMENTS) {
      ctx.strokeStyle = s.kind === 'panel' ? 'rgba(120,160,255,0.35)' : 'rgba(110,160,255,0.5)';
      ctx.beginPath(); ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); ctx.stroke();
    }
    ctx.fillStyle = 'rgba(80,120,220,0.14)';
    ctx.fillRect(LCD.x1, LCD.y1, LCD.x2 - LCD.x1, LCD.y2 - LCD.y1);
    ctx.fillStyle = 'rgba(150,190,255,0.5)';
    ctx.font = '700 12px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('editor.lcdFixed'), 225, 250);

    // 仕切り
    for (const s of L.separators) {
      const on = this.sel?.obj === s;
      ctx.strokeStyle = on ? '#ffffff' : '#6ea8ff';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(s.x - s.r, s.y2); ctx.lineTo(s.x - s.r, s.y1);
      ctx.arc(s.x, s.y1, s.r, Math.PI, 0);
      ctx.lineTo(s.x + s.r, s.y2);
      ctx.stroke();
      if (on) { this._node(ctx, s.x, s.y1); this._node(ctx, s.x, s.y2); }
    }

    // 壁
    for (const w of L.walls) {
      const on = this.sel?.obj === w;
      ctx.strokeStyle = on ? '#ffffff' : '#7fd4ff';
      ctx.lineWidth = on ? 4 : 3;
      ctx.beginPath(); ctx.moveTo(w.x1, w.y1); ctx.lineTo(w.x2, w.y2); ctx.stroke();
      if (on) { this._node(ctx, w.x1, w.y1); this._node(ctx, w.x2, w.y2); }
    }
    if (this.draft) {
      ctx.strokeStyle = 'rgba(127,212,255,0.7)';
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(this.draft.x1, this.draft.y1); ctx.lineTo(this.draft.x2, this.draft.y2); ctx.stroke();
      ctx.setLineDash([]);
    }

    // 入賞口
    for (const d of POCKET_DEFS) {
      const pos = L.pockets[d.id];
      const on = this.sel?.obj === pos;
      ctx.strokeStyle = d.id === 'attacker' ? '#ff6ec7' : '#ffd166';
      ctx.lineWidth = on ? 4 : 3;
      for (const line of d.cup) {
        ctx.beginPath();
        ctx.moveTo(pos.x + line[0][0], pos.y + line[0][1]);
        ctx.lineTo(pos.x + line[1][0], pos.y + line[1][1]);
        ctx.stroke();
      }
      if (d.gate) {
        ctx.strokeStyle = 'rgba(255,110,199,0.6)';
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(pos.x + d.gate[0][0], pos.y + d.gate[0][1]);
        ctx.lineTo(pos.x + d.gate[1][0], pos.y + d.gate[1][1]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.fillStyle = on ? '#ffffff' : 'rgba(255,255,255,0.6)';
      ctx.font = '700 10px system-ui, sans-serif';
      ctx.fillText(t(d.labelKey), pos.x, pos.y + 36);
      if (on) this._node(ctx, pos.x, pos.y);
    }

    // 風車
    for (const wm of L.windmills) {
      const on = this.sel?.obj === wm;
      ctx.strokeStyle = on ? '#ffffff' : '#b39dff';
      ctx.lineWidth = 3;
      for (let i = 0; i < wm.blades; i++) {
        const a = wm.phase + (i * TAU) / wm.blades;
        ctx.beginPath();
        ctx.moveTo(wm.x + Math.cos(a) * 4, wm.y + Math.sin(a) * 4);
        ctx.lineTo(wm.x + Math.cos(a) * wm.r, wm.y + Math.sin(a) * wm.r);
        ctx.stroke();
      }
    }

    // 釘
    for (const g of L.pegs) {
      const on = this.sel?.obj === g;
      ctx.fillStyle = on ? '#ffffff' : g.type === 'guide' ? '#ffd166' : '#c9d8ff';
      ctx.beginPath(); ctx.arc(g.x, g.y, PEG_R + (on ? 1.5 : 0), 0, TAU); ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.4)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  _node(ctx, x, y) {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath(); ctx.arc(x, y, 4.5, 0, TAU); ctx.fill();
    ctx.strokeStyle = '#1b2440';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  // ---------------------------------------------------------------- 右側のパネル

  _syncTabs() {
    for (const b of this.root.querySelectorAll('.ed-tabs button')) {
      b.classList.toggle('on', b.dataset.tab === this.tab);
    }
    this._clearPreviews();
    if (this.tab === 'board') this._paneBoard();
    else if (this.tab === 'look') this._paneLook();
    else if (this.tab === 'spec') this._paneSpec();
    else this._paneVideo();
  }

  _paneBoard() {
    const L = this.m.layout;
    const notes = inspect(L);
    const d = this.m.diag;
    const count = (label, n, unit) =>
      `<dt>${esc(t(`editor.count.${label}`))}</dt><dd>${esc(unit ? t(unit, { n: num(n) }) : num(n))}</dd>`;

    this.pane.innerHTML = `
      <div class="ed-tools">
        ${TOOLS.map((id) => `<button type="button" data-tool="${id}"
          class="${id === this.tool ? 'on' : ''}">${esc(t(`editor.tool.${id}`))}</button>`).join('')}
      </div>
      <p class="ed-hint">${esc(t(`editor.tool.${this.tool}.hint`))}</p>
      <div class="ed-row">
        <label><input type="checkbox" class="ed-grid" ${this.grid ? 'checked' : ''}> ${esc(t('editor.grid', { n: GRID }))}</label>
        <label><input type="checkbox" class="ed-mirror" ${this.mirror ? 'checked' : ''}> ${esc(t('editor.mirror'))}</label>
      </div>
      <div class="ed-row">
        <button class="ed-btn sm ed-undo" type="button" ${this.history.length ? '' : 'disabled'}>${esc(t('editor.undo'))}</button>
        <button class="ed-btn sm ed-redo" type="button" ${this.future.length ? '' : 'disabled'}>${esc(t('editor.redo'))}</button>
        <button class="ed-btn sm ed-del" type="button" ${this.sel && this.sel.kind !== 'pocket' ? '' : 'disabled'}>${esc(t('editor.delSel'))}</button>
      </div>
      <dl class="ed-counts">
        ${count('pegs', L.pegs.length, 'editor.count.sticks')}
        ${count('walls', L.walls.length, 'editor.count.sticks')}
        ${count('separators', L.separators.length)}
        ${count('windmills', L.windmills.length)}
      </dl>

      <h3 class="ed-h">${esc(t('editor.gen.title'))}</h3>
      <p class="ed-note">${esc(t('editor.gen.note'))}</p>
      <div class="ed-row">
        <input type="range" class="ed-gap" min="13.6" max="21" step="0.2" value="${this.m.nailGap}">
        <span class="ed-gapv">${(+this.m.nailGap).toFixed(1)}px</span>
      </div>
      <button class="ed-btn sm wide ed-gen" type="button">${esc(t('editor.gen.btn'))}</button>

      <h3 class="ed-h">${esc(t('editor.check.title'))}</h3>
      ${notes.length
        ? `<ul class="ed-notes">${notes.map((n) =>
            `<li class="${n.level}">${esc(t(n.key, n.vars))}</li>`).join('')}</ul>`
        : `<p class="ed-note ok">${esc(t('editor.check.clean'))}</p>`}

      <h3 class="ed-h">${esc(t('editor.diag.title'))}</h3>
      <p class="ed-note">${esc(t('editor.diag.note'))}</p>
      <button class="ed-btn sm wide ed-diag" type="button" ${this.diagBusy ? 'disabled' : ''}>
        ${esc(this.diagBusy ? t('editor.diag.busy') : t('editor.diag.btn', { n: DIAG_BALLS * 2 }))}</button>
      <div class="ed-diagout">${d ? diagHtml(d) : ''}</div>`;

    for (const b of this.pane.querySelectorAll('[data-tool]')) {
      b.addEventListener('click', () => { this.tool = b.dataset.tool; this.sel = null; this._syncTabs(); this._render(); });
    }
    this.pane.querySelector('.ed-grid').addEventListener('change', (e) => {
      this.grid = e.target.checked; this._render();
    });
    this.pane.querySelector('.ed-mirror').addEventListener('change', (e) => {
      this.mirror = e.target.checked; this._render();
    });
    this.pane.querySelector('.ed-undo').addEventListener('click', () => this._undo());
    this.pane.querySelector('.ed-redo').addEventListener('click', () => this._redo());
    this.pane.querySelector('.ed-del').addEventListener('click', () => this._deleteSelection());

    const gap = this.pane.querySelector('.ed-gap');
    gap.addEventListener('input', () => {
      this.pane.querySelector('.ed-gapv').textContent = (+gap.value).toFixed(1) + 'px';
    });
    this.pane.querySelector('.ed-gen').addEventListener('click', () => {
      if (!confirm(t('editor.confirmGen'))) return;
      this._snapshot();
      this.m.nailGap = +gap.value;
      this.m.layout = defaultLayout({ nailGap: +gap.value });
      this.sel = null;
      this._render();
      this._syncTabs();
    });
    this.pane.querySelector('.ed-diag').addEventListener('click', () => this._diagnose());
  }

  _paneLook() {
    const m = this.m;
    const field = (k, inner) =>
      `<label class="ed-f"><span>${esc(t(`editor.look.${k}`))}</span>${inner}</label>`;

    this.pane.innerHTML = `
      ${field('name', '<input type="text" class="ed-i" data-k="name" maxlength="24" value="' + esc(m.name) + '">')}
      ${field('short', '<input type="text" class="ed-i" data-k="short" maxlength="6" value="' + esc(m.short) + '">')}
      ${field('type', '<input type="text" class="ed-i" data-k="type" maxlength="8" value="' + esc(m.type) + '">')}
      ${field('blurb', '<input type="text" class="ed-i" data-k="blurb" maxlength="60" value="' + esc(m.blurb) + '">')}
      <div class="ed-f"><span>${esc(t('editor.look.icon'))}</span>
        <div class="ed-icons">${ICON_CHOICES.map((c) => `<button type="button" data-icon="${c}"
          class="${c === m.icon ? 'on' : ''}">${c}</button>`).join('')}</div></div>
      ${field('color', '<input type="color" class="ed-c" data-k="color" value="' + m.color + '">')}
      ${field('accent', '<input type="color" class="ed-c" data-k="accent" value="' + m.accent + '">')}
      <label class="ed-f"><span>${esc(t('editor.look.hue'))} <b class="ed-huev">${m.hue}</b></span>
        <input type="range" class="ed-hue" min="0" max="359" value="${m.hue}"></label>
      <div class="ed-swatch" style="--a:${m.color};--b:${m.accent};--h:${m.hue}">
        <span>${esc(m.icon)}</span><b>${esc(m.name)}</b>
      </div>`;

    for (const el of this.pane.querySelectorAll('.ed-i, .ed-c')) {
      el.addEventListener('input', () => {
        this.m[el.dataset.k] = el.value;
        // 主の色を変えたら背景の色みも合わせる（別々に触ると濁りやすい）
        if (el.dataset.k === 'color') {
          const h = hueOf(el.value);
          if (h !== null) {
            this.m.hue = h;
            this.pane.querySelector('.ed-hue').value = h;
            this.pane.querySelector('.ed-huev').textContent = h;
          }
        }
        if (el.dataset.k === 'name') this.nameEl.value = el.value;
        this._syncSwatch();
        this._render();
      });
    }
    for (const b of this.pane.querySelectorAll('[data-icon]')) {
      b.addEventListener('click', () => {
        this.m.icon = b.dataset.icon;
        for (const o of this.pane.querySelectorAll('[data-icon]')) o.classList.toggle('on', o === b);
        this._syncSwatch();
      });
    }
    const hue = this.pane.querySelector('.ed-hue');
    hue.addEventListener('input', () => {
      this.m.hue = +hue.value;
      this.pane.querySelector('.ed-huev').textContent = hue.value;
      this._syncSwatch();
      this._render();
    });
  }

  _syncSwatch() {
    const s = this.pane.querySelector('.ed-swatch');
    if (!s) return;
    s.style.setProperty('--a', this.m.color);
    s.style.setProperty('--b', this.m.accent);
    s.style.setProperty('--h', this.m.hue);
    s.querySelector('span').textContent = this.m.icon;
    s.querySelector('b').textContent = this.m.name;
  }

  _paneSpec() {
    const m = this.m;
    // [鍵, 最小, 最大, 刻み, 見せ方]
    const rows = [
      ['odds', 2, 400, 1, (v) => t('panel.oddsValue', { n: v })],
      ['rounds', 1, 16, 1, (v) => t('panel.roundsValue', { n: v })],
      ['premiumRounds', 1, 16, 1, (v) => t('panel.roundsValue', { n: v })],
      ['roundMaxBalls', 1, 12, 1, (v) => t('unit.pieces', { n: v })],
      ['roundTime', 300, 3000, 60, (v) => t('editor.spec.seconds', { n: (v / 60).toFixed(0) })],
      ['fireInterval', 12, 60, 1, (v) => t('editor.spec.perMinute', { n: Math.round(3600 / v) })],
      ['reachChance', 0, 0.6, 0.01, (v) => `${(v * 100).toFixed(0)}%`],
      ['superReachChance', 0, 1, 0.01, (v) => `${(v * 100).toFixed(0)}%`],
    ];
    const awards = ['start', 'side', 'attacker'];
    const awardLabel = { start: 'pocket.start', side: 'pocket.side', attacker: 'pocket.attacker' };

    this.pane.innerHTML = `
      ${rows.map(([k, min, max, step, fmt]) => `
        <label class="ed-f"><span>${esc(t(`editor.spec.${k}`))} <b data-v="${k}">${esc(fmt(m[k]))}</b></span>
          <input type="range" data-s="${k}" min="${min}" max="${max}" step="${step}" value="${m[k]}"></label>`).join('')}
      <h3 class="ed-h">${esc(t('editor.spec.awards'))}</h3>
      ${awards.map((k) => `
        <label class="ed-f"><span>${esc(t(awardLabel[k]))} <b data-av="${k}">${m.awards[k]}</b></span>
          <input type="range" data-a="${k}" min="0" max="20" step="1" value="${m.awards[k]}"></label>`).join('')}
      <p class="ed-note ed-payout">${esc(t('editor.spec.payoutNote', { n: num(estimatePayout(m)) }))}</p>`;

    const syncPayout = () => {
      this.pane.querySelector('.ed-payout').textContent =
        t('editor.spec.payoutNote', { n: num(estimatePayout(this.m)) });
    };
    for (const el of this.pane.querySelectorAll('[data-s]')) {
      const row = rows.find((r) => r[0] === el.dataset.s);
      el.addEventListener('input', () => {
        this.m[el.dataset.s] = +el.value;
        if (el.dataset.s === 'rounds' && this.m.premiumRounds < this.m.rounds) {
          this.m.premiumRounds = this.m.rounds;
          const pr = this.pane.querySelector('[data-s="premiumRounds"]');
          pr.value = this.m.premiumRounds;
          this.pane.querySelector('[data-v="premiumRounds"]').textContent =
            t('panel.roundsValue', { n: this.m.premiumRounds });
        }
        this.pane.querySelector(`[data-v="${el.dataset.s}"]`).textContent = row[4](+el.value);
        syncPayout();
      });
    }
    for (const el of this.pane.querySelectorAll('[data-a]')) {
      el.addEventListener('input', () => {
        this.m.awards[el.dataset.a] = +el.value;
        this.pane.querySelector(`[data-av="${el.dataset.a}"]`).textContent = el.value;
        syncPayout();
      });
    }
  }

  _paneVideo() {
    const m = this.m;
    const label = (v) => {
      if (!v) return t('editor.video.none');
      if (v.kind === 'builtin') return builtinName(v.builtinId);
      return v.name + (v.size ? `（${mb(v.size)}）` : '');
    };

    this.pane.innerHTML = `
      <p class="ed-note">${esc(t('editor.video.note'))}</p>
      ${VIDEO_STATES.map(({ id }) => {
        const v = m.videos[id];
        return `
        <div class="ed-vid" data-state="${id}">
          <div class="ed-vid-head"><b>${esc(videoStateLabel(id))}</b>
            <span>${esc(label(v))}</span></div>
          <div class="ed-row">
            <select class="ed-vsel">
              <option value="">${esc(t('editor.video.none'))}</option>
              ${BUILTIN_VIDEOS.map((b) => `<option value="${b.src}"
                ${v?.kind === 'builtin' && v.src === b.src ? 'selected' : ''}>${esc(builtinName(b.id))}</option>`).join('')}
              ${v?.kind === 'blob' ? `<option value="__blob" selected>${esc(v.name)}</option>` : ''}
            </select>
            <label class="ed-btn sm file">${esc(t('editor.video.pick'))}<input type="file" accept="video/*" hidden></label>
          </div>
          <div class="ed-vprev"></div>
        </div>`;
      }).join('')}
      <p class="ed-note">${esc(t('editor.video.sizeNote'))}</p>`;

    for (const box of this.pane.querySelectorAll('.ed-vid')) {
      const state = box.dataset.state;
      const sel = box.querySelector('.ed-vsel');
      sel.addEventListener('change', () => {
        if (sel.value === '__blob') return;
        if (!sel.value) delete this.m.videos[state];
        else {
          const b = BUILTIN_VIDEOS.find((x) => x.src === sel.value);
          this.m.videos[state] = { kind: 'builtin', src: b.src, builtinId: b.id };
        }
        delete this.pendingFiles[state];
        this._syncTabs();
      });
      box.querySelector('input[type=file]').addEventListener('change', (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith('video/')) { alert(t('editor.video.notVideo')); return; }
        this.pendingFiles[state] = f;
        this.m.videos[state] = {
          kind: 'blob', key: videoKey(this.m.id, state), name: f.name, size: f.size,
        };
        this._syncTabs();
      });
      this._preview(box.querySelector('.ed-vprev'), state);
    }
  }

  // 選んだ映像をその場で確認できるようにする
  _preview(host, state) {
    const v = this.m.videos[state];
    if (!v) return;
    let src = null;
    // 同梱の素材は言語ディレクトリのぶん上に置いてある
    if (v.kind === 'builtin') src = assetUrl(v.src);
    else if (this.pendingFiles[state]) {
      src = URL.createObjectURL(this.pendingFiles[state]);
      this.previewUrls.push(src);
    }
    if (!src) {
      host.innerHTML = `<span class="ed-note">${esc(t('editor.video.saved'))}</span>`;
      return;
    }
    const el = document.createElement('video');
    el.src = src;
    el.muted = true;
    el.loop = true;
    el.playsInline = true;
    el.preload = 'metadata';
    el.controls = true;
    host.append(el);
  }

  _clearPreviews() {
    for (const u of this.previewUrls) URL.revokeObjectURL(u);
    this.previewUrls = [];
  }

  // ---------------------------------------------------------------- 診断

  /**
   * 実際に玉を転がして測る。physics.js はブラウザ API に依存しないので、
   * 盤面をそのまま回せる。一気に回すと画面が固まるので少しずつ進める。
   */
  async _diagnose() {
    if (this.diagBusy) return;
    this.diagBusy = true;
    this._syncTabs();

    const out = this.pane.querySelector('.ed-diagout');
    const N = DIAG_BALLS;
    const MAX_FRAMES = 4000;
    const res = {
      left: { start: 0, side: 0, atk: 0, stuck: 0 },
      right: { start: 0, side: 0, atk: 0, stuck: 0 },
      n: N,
    };

    for (const [side, dial, gateOpen] of [['left', 0.2, false], ['right', 0.85, true]]) {
      const power = dialToPower(dial);
      for (let i = 0; i < N; i++) {
        const board = createBoard(this.m);
        board.gateOpen = gateOpen;
        const ball = launchBall(power, Math.random());
        const balls = [ball];
        let f = 0;
        while (ball.alive && f < MAX_FRAMES) {
          const ev = [];
          stepWorld(board, balls, ev);
          for (const e of ev) {
            if (e.type !== 'pocket') continue;
            if (e.pocket.id === 'start') res[side].start++;
            else if (e.pocket.id === 'attacker') res[side].atk++;
            else res[side].side++;
          }
          f++;
        }
        if (ball.alive || f >= 2000) res[side].stuck++;
        if (i % 5 === 4) {
          if (out) {
            out.innerHTML = `<p class="ed-note">${esc(t('editor.diag.progress', {
              side: t(`editor.diag.${side}`), i: i + 1, n: N,
            }))}</p>`;
          }
          await yieldToUi();
          if (!this.visible) { this.diagBusy = false; return; }
        }
      }
    }

    const startPct = (res.left.start / N) * 100;
    this.m.diag = {
      startPct,
      atkPct: (res.right.atk / N) * 100,
      stuck: res.left.stuck + res.right.stuck,
      n: N,
    };
    // 測った回りかたを釘の評価としてホールの一覧に出す
    this.m.nail = startPct >= 9 ? 'loose' : startPct >= 4 ? 'normal' : 'tight';
    this.diagBusy = false;
    this._syncTabs();
  }
}

// ---------------------------------------------------------------- 補助

function diagHtml(d) {
  const warn = [];
  if (d.stuck) warn.push(t('editor.diag.warnStuck', { n: d.stuck }));
  if (d.startPct < 2) warn.push(t('editor.diag.warnLow'));
  if (d.startPct > 14) warn.push(t('editor.diag.warnHigh'));
  if (d.atkPct < 15) warn.push(t('editor.diag.warnAtk'));
  return `
    <dl class="ed-counts">
      <dt>${esc(t('editor.diag.start'))}</dt><dd>${d.startPct.toFixed(1)}%</dd>
      <dt>${esc(t('editor.diag.atk'))}</dt><dd>${d.atkPct.toFixed(1)}%</dd>
      <dt>${esc(t('editor.diag.stuck'))}</dt><dd>${esc(t('unit.balls', { n: d.stuck }))}</dd>
    </dl>
    ${warn.length
      ? `<ul class="ed-notes">${warn.map((w) => `<li class="warn">${esc(w)}</li>`).join('')}</ul>`
      : `<p class="ed-note ok">${esc(t('editor.diag.ok'))}</p>`}`;
}

// 測定の合間に画面を描かせる。requestAnimationFrame にすると、裏のタブに
// 回った瞬間にフレームが止まって測定も止まってしまうのでタイマーで刻む。
const yieldToUi = () => new Promise((r) => setTimeout(r, 0));

function mirrorWall(w) {
  return {
    x1: r1(MIRROR_AXIS * 2 - w.x1), y1: w.y1,
    x2: r1(MIRROR_AXIS * 2 - w.x2), y2: w.y2,
  };
}

function distToSeg(px, py, s) {
  const ax = s.x1, ay = s.y1;
  const bx = s.x2 - ax, by = s.y2 - ay;
  const len2 = bx * bx + by * by;
  let t = len2 > 0 ? ((px - ax) * bx + (py - ay) * by) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + bx * t), py - (ay + by * t));
}

/** #rrggbb から色相を取り出す。色を選んだら背景の色みも揃えるのに使う */
function hueOf(hex) {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return null;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16) / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (d === 0) return 0;
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return Math.round(((h * 60) + 360) % 360);
}

const mb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

function esc(s) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
